import { ColumnDef } from "@tanstack/react-table";
import { format, isValid } from "date-fns";
import { simpleDate, toCalendarDate, toLocalDate } from "@/utils/datetime";

/**
 * Date buckets for grouping by a date column. Grouping on the raw date gives one
 * group per distinct day, which on most tables is close to one group per row, so a
 * column that opts in through `meta.groupByMonth` or `meta.dateBucket` groups on its
 * month, quarter or year instead.
 */

export const isMonthGroupedColumn = (meta: unknown): boolean =>
  Boolean((meta as { groupByMonth?: boolean } | undefined)?.groupByMonth);

/**
 * A column whose cells show a timestamp's calendar day (`meta.localDay`): the local day
 * for a timed value, the stored day for a date-only one, as `toCalendarDate` decides.
 */
export const isLocalDayColumn = (meta: unknown): boolean =>
  Boolean((meta as { localDay?: boolean } | undefined)?.localDay);

const MONTH_KEY_PATTERN = /^(\d{4})-(\d{2})/;

/**
 * `YYYY-MM` for a date value, or "" when it holds no date (the blank group). A
 * local-day column buckets by the viewer's local month, matching the day its cell
 * shows; any other column by the stored calendar date.
 */
export const toMonthGroupKey = (value: unknown, isLocalDay = false): string => {
  if (typeof value !== "string") return "";
  const match = value.match(MONTH_KEY_PATTERN);
  if (!match) return "";
  const storedMonth = `${match[1]}-${match[2]}`;
  if (!isLocalDay) return storedMonth;
  const day = toCalendarDate(value);
  return isValid(day) ? format(day, "yyyy-MM") : storedMonth;
};

/** The value a column reads off a row, through its accessor function or key. */
export const getColumnRawValue = (column: unknown, row: unknown): unknown => {
  const { accessorFn, accessorKey } = column as {
    accessorFn?: (row: unknown, index: number) => unknown;
    accessorKey?: string;
  };
  if (accessorFn) return accessorFn(row, 0);
  return accessorKey
    ? (row as Record<string, unknown> | undefined)?.[accessorKey]
    : undefined;
};

/** The month key a column's value on a row falls in. */
export const columnMonthGroupKey = (column: unknown, row: unknown): string =>
  toMonthGroupKey(
    getColumnRawValue(column, row),
    isLocalDayColumn((column as { meta?: unknown } | undefined)?.meta)
  );

/**
 * Ranks a row by the month a column's date falls in (202603), so month groups come out in
 * calendar order whatever the rows are sorted by. A row with no date ranks null, last.
 */
export const monthGroupRank =
  (column: unknown) =>
  (row: unknown): number | null => {
    const monthKey = columnMonthGroupKey(column, row);
    return monthKey ? Number(monthKey.replace("-", "")) : null;
  };

/** "Mar 2026" for a `YYYY-MM` key. */
export const formatMonthGroupLabel = (monthKey: string): string =>
  simpleDate(toLocalDate(`${monthKey}-01`), "short", false, false, true);

/**
 * Month, quarter and year buckets. A month bucket keeps the date column's own id, so a
 * view saved before quarters and years existed keeps its grouping; a quarter or year
 * bucket is a column of its own, `<column id>__quarter` or `<column id>__year`.
 */
export type DateBucket = "month" | "quarter" | "year";

export const DATE_BUCKETS: readonly DateBucket[] = ["month", "quarter", "year"];

export const DATE_BUCKET_SEPARATOR = "__";

export interface DateBucketMeta {
  dateBucket?: DateBucket;
  groupByMonth?: boolean;
  groupDateOf?: (raw: unknown) => string | null;
}

/** The bucket a column groups its dates by, or undefined for a column that does not. */
export const getDateBucket = (meta: unknown): DateBucket | undefined => {
  const bucketMeta = meta as DateBucketMeta | undefined;
  if (bucketMeta?.dateBucket) return bucketMeta.dateBucket;
  return bucketMeta?.groupByMonth ? "month" : undefined;
};

/**
 * `YYYY-MM`, `YYYY-Q#` or `YYYY` for a date value, or "" when it holds no date. The
 * quarter and year follow the month key, so a local-day column buckets by the viewer's
 * local quarter and year just as it does by month.
 */
export const toDateBucketKey = (
  value: unknown,
  bucket: DateBucket,
  isLocalDay = false
): string => {
  const monthKey = toMonthGroupKey(value, isLocalDay);
  if (!monthKey || bucket === "month") return monthKey;
  const [year, month] = monthKey.split("-");
  if (bucket === "year") return year;
  const monthNumber = Number(month);
  if (monthNumber < 1 || monthNumber > 12) return "";
  return `${year}-Q${Math.floor((monthNumber - 1) / 3) + 1}`;
};

/**
 * The bucket key a column's value on a row falls in. A column whose value is not a date
 * itself, such as a date range, names the date it groups by through `meta.groupDateOf`.
 */
export const columnDateBucketKey = (column: unknown, row: unknown): string => {
  const meta = (column as { meta?: unknown } | undefined)?.meta;
  const bucket = getDateBucket(meta);
  if (!bucket) return "";
  const raw = getColumnRawValue(column, row);
  const groupDateOf = (meta as DateBucketMeta | undefined)?.groupDateOf;
  return toDateBucketKey(
    groupDateOf ? groupDateOf(raw) : raw,
    bucket,
    isLocalDayColumn(meta)
  );
};

/**
 * Chronological rank for a bucket key (202603, a year's quarters as year * 4 + quarter - 1,
 * or the year itself), so buckets the server reported but whose rows are not loaded yet
 * still sort into place. "" ranks null, last.
 */
export const dateBucketRank = (
  key: string,
  bucket: DateBucket
): number | null => {
  if (bucket === "month") {
    return /^\d{4}-\d{2}$/.test(key) ? Number(key.replace("-", "")) : null;
  }
  if (bucket === "quarter") {
    const parts = key.match(/^(\d{4})-Q([1-4])$/);
    return parts ? Number(parts[1]) * 4 + Number(parts[2]) - 1 : null;
  }
  return /^\d{4}$/.test(key) ? Number(key) : null;
};

/** "Mar 2026", "Q1 2026" or "2026" for a bucket key. */
export const formatDateBucketLabel = (
  key: string,
  bucket: DateBucket
): string => {
  if (bucket === "month") return formatMonthGroupLabel(key);
  if (bucket === "quarter") {
    const parts = key.match(/^(\d{4})-Q([1-4])$/);
    return parts ? `Q${parts[2]} ${parts[1]}` : key;
  }
  return key;
};

/**
 * The date column and bucket a group-by id names, for a quarter or year id. A bare column
 * id is its month bucket and returns undefined, so callers keep treating it as the column.
 */
export const parseDateBucketId = (
  id: string
): { baseId: string; bucket: Exclude<DateBucket, "month"> } | undefined => {
  const separatorAt = id.lastIndexOf(DATE_BUCKET_SEPARATOR);
  if (separatorAt <= 0) return undefined;
  const bucket = id.slice(separatorAt + DATE_BUCKET_SEPARATOR.length);
  if (bucket !== "quarter" && bucket !== "year") return undefined;
  return { baseId: id.slice(0, separatorAt), bucket };
};

/**
 * A grouping-only column for a quarter or year bucket of a date column. The table groups
 * on it while the date column itself keeps showing the full date.
 */
export const buildDateBucketColumn = <TData>({
  baseId,
  bucket,
  accessorFn,
  localDay,
  groupDateOf,
  header
}: {
  baseId: string;
  bucket: Exclude<DateBucket, "month">;
  accessorFn: (row: TData) => unknown;
  localDay?: boolean;
  groupDateOf?: (raw: unknown) => string | null;
  header?: ColumnDef<TData>["header"];
}): ColumnDef<TData> => ({
  id: `${baseId}${DATE_BUCKET_SEPARATOR}${bucket}`,
  accessorFn,
  header,
  enableSorting: false,
  meta: { dateBucket: bucket, localDay, groupDateOf }
});
